// =============================================================================
// Notification utilities
// Provides sound notifications for various events
// =============================================================================

// Notification sound URL - using a simple, pleasant notification sound
// This is a base64 encoded short beep sound to avoid external dependencies
const NOTIFICATION_SOUND_BASE64 =
  "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAABYYoRwmHAAAAAAD/+9DEAAAGAAGn9AAAIjSLLv80oAgAAIHAfD4Pg+H/EIQ//ygTB8Hw/B8H/+XB8HwfD/y4f/l/5cPh//8uH//lwfD5f+UAAACytraWltbT09PT09PT0+fn5+fn5+jo6Ojo6Ojo+vr6+vr6+vr7+/v7+/v7/Pz8/Pz8/P39/f39/f3+/v7+/v7+////////////////////////////////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

let audioContext: AudioContext | null = null;
let notificationBuffer: AudioBuffer | null = null;

// Initialize audio context (must be called after user interaction)
async function initAudio(): Promise<void> {
  if (audioContext) return;

  try {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // Decode the base64 audio
    const response = await fetch(NOTIFICATION_SOUND_BASE64);
    const arrayBuffer = await response.arrayBuffer();
    notificationBuffer = await audioContext.decodeAudioData(arrayBuffer);
  } catch (error) {
    console.warn('[notifications] Failed to initialize audio:', error);
  }
}

/**
 * Plays a notification sound
 * Falls back silently if audio is not available
 */
export async function playNotificationSound(): Promise<void> {
  try {
    // Try to initialize if not already done
    if (!audioContext) {
      await initAudio();
    }

    if (!audioContext || !notificationBuffer) {
      // Fallback to simple Audio element
      const audio = new Audio(NOTIFICATION_SOUND_BASE64);
      audio.volume = 0.5;
      await audio.play().catch(() => {});
      return;
    }

    // Resume if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    // Create and play the sound
    const source = audioContext.createBufferSource();
    source.buffer = notificationBuffer;

    const gainNode = audioContext.createGain();
    gainNode.gain.value = 0.5; // 50% volume

    source.connect(gainNode);
    gainNode.connect(audioContext.destination);
    source.start(0);
  } catch (error) {
    // Silently fail - notifications are non-critical
    console.debug('[notifications] Could not play sound:', error);
  }
}

/**
 * Shows a browser notification (if permitted) with optional sound
 */
export async function showBrowserNotification(
  title: string,
  options?: NotificationOptions & { playSound?: boolean }
): Promise<void> {
  const { playSound = true, ...notificationOptions } = options || {};

  // Play sound if requested
  if (playSound) {
    await playNotificationSound();
  }

  // Check if browser notifications are supported and permitted
  if (!('Notification' in window)) {
    return;
  }

  try {
    if (Notification.permission === 'granted') {
      new Notification(title, notificationOptions);
    } else if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        new Notification(title, notificationOptions);
      }
    }
  } catch (error) {
    // Notifications might not work in some contexts (e.g., iframes)
    console.debug('[notifications] Browser notification failed:', error);
  }
}

/**
 * Notifies user that a report is ready
 * Shows toast, plays sound, and optionally shows browser notification
 */
export async function notifyReportReady(
  reportName: string,
  options?: { browserNotification?: boolean }
): Promise<void> {
  const { browserNotification = false } = options || {};

  // Always play sound
  await playNotificationSound();

  // Show browser notification if requested and page is not focused
  if (browserNotification && document.hidden) {
    await showBrowserNotification('Reporte Listo', {
      body: `El reporte "${reportName}" está listo para descargar`,
      icon: '/favicon.ico',
      playSound: false, // Already played above
    });
  }
}

/**
 * Notifies user of batch completion
 */
export async function notifyBatchComplete(
  successCount: number,
  failedCount: number
): Promise<void> {
  await playNotificationSound();

  if (document.hidden) {
    const body = failedCount > 0
      ? `${successCount} completados, ${failedCount} fallidos`
      : `${successCount} reportes generados exitosamente`;

    await showBrowserNotification('Lote Completado', {
      body,
      icon: '/favicon.ico',
      playSound: false,
    });
  }
}
